import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, Globe, Instagram, Clock, ArrowLeft, Star, Camera, Loader2, ArrowUpRight, Utensils, CalendarDays, Heart } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/api";
import { EventCard } from "@/components/Cards";
import FollowButton from "@/components/FollowButton";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function Stars({ value, size = 14, onSet }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} onClick={onSet ? () => onSet(n) : undefined}
          className={`${onSet ? "cursor-pointer" : ""} ${n <= value ? "text-[#FFEA00]" : "text-neutral-600"}`}
          fill={n <= value ? "#FFEA00" : "none"} />
      ))}
    </div>
  );
}

function Reviews({ slug }) {
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/businesses/${slug}/reviews`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [slug]);

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/reviews/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, res.id]);
      toast.success("Photo added");
    } catch { toast.error("Upload failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Add your name"); return; }
    setBusy(true);
    try {
      await api.post(`/businesses/${slug}/reviews`, { ...form, rating: Number(form.rating), photo_ids: photos });
      toast.success("Thanks for your review!");
      setForm({ name: "", rating: 5, text: "" }); setPhotos([]); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-12" data-testid="reviews-section">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="font-display font-bold text-2xl text-white">Reviews</h2>
        {data.count > 0 && <div className="flex items-center gap-2"><Stars value={Math.round(data.average)} /><span className="text-neutral-400 text-sm">{data.average} ({data.count})</span></div>}
      </div>

      <form onSubmit={submit} className="border border-white/10 bg-[#141414] p-5 mb-6 space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-neutral-400 text-sm">Your rating:</span>
          <Stars value={form.rating} size={20} onSet={(n) => setForm({ ...form, rating: n })} />
        </div>
        <input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]" />
        <textarea rows={3} placeholder="Share your experience..." value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="review-text" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]" />
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-neutral-400 text-sm cursor-pointer border border-white/15 px-3 py-2 hover:border-[#00E5FF]">
            <Camera size={15} /> Add photos
            <input type="file" accept="image/*" onChange={uploadPhoto} data-testid="review-photo" className="hidden" />
          </label>
          <div className="flex gap-2">
            {photos.map((id) => <img key={id} src={`${API}/images/${id}/file`} alt="" className="w-10 h-10 object-cover border border-white/15" />)}
          </div>
        </div>
        <Button type="submit" disabled={busy} data-testid="review-submit" className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none">{busy ? "Posting..." : "Post Review"}</Button>
      </form>

      <div className="space-y-4">
        {data.reviews.map((r) => (
          <div key={r.id} className="border border-white/10 bg-[#141414] p-4" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-white font-medium">{r.name}</span>
              <Stars value={r.rating} />
            </div>
            {r.text && <p className="text-neutral-400 text-sm mt-2">{r.text}</p>}
            {r.photo_ids?.length > 0 && (
              <div className="flex gap-2 mt-3">
                {r.photo_ids.map((id) => <img key={id} src={`${API}/images/${id}/file`} alt="" className="w-20 h-20 object-cover border border-white/10" />)}
              </div>
            )}
          </div>
        ))}
        {data.reviews.length === 0 && <div className="text-neutral-500 text-sm">No reviews yet — be the first!</div>}
      </div>
    </div>
  );
}

export default function BusinessDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [guides, setGuides] = useState([]);
  const [events, setEvents] = useState([]);
  const [vmedia, setVmedia] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [faved, setFaved] = useState(false);
  const isCustomer = user && user.role === "customer";

  useEffect(() => {
    api.get(`/businesses/${slug}`).then((r) => { setB(r.data); track("business", r.data.id, "view"); }).catch(() => setNotFound(true));
    api.get(`/businesses/${slug}/guides`).then((r) => setGuides(r.data)).catch(() => {});
    api.get(`/businesses/${slug}/events`).then((r) => setEvents(r.data)).catch(() => {});
    api.get(`/venues/${slug}/media`).then((r) => setVmedia(r.data)).catch(() => {});
    if (isCustomer) api.get("/me/favorites").then((r) => setFaved(r.data.some((x) => x.slug === slug))).catch(() => {});
  }, [slug, isCustomer]);

  const toggleFav = async () => {
    if (!isCustomer) { toast("Sign in to save favorites"); return; }
    const { data } = await api.post(`/me/favorites/${slug}`);
    setFaved(data.favorited);
    toast.success(data.favorited ? "Saved to favorites" : "Removed");
  };

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-neutral-400">Business not found. <Link to="/directory" className="text-[#FF0055]">Back to directory</Link></div>;
  if (!b) return <div className="py-32 text-center text-neutral-500">Loading...</div>;

  const gallery = (b.gallery && b.gallery.length ? b.gallery : [b.image]).filter(Boolean);
  const videos = vmedia?.videos || [];
  const geoVerified = (vmedia?.gallery || []).some((m) => m.geo) || videos.some((m) => m.geo);
  const absImg = b.image?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${b.image}` : b.image;
  const SITE = "https://visitrino.com";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": `${SITE}/directory/${b.slug}`,
        name: b.name,
        description: b.description,
        image: absImg,
        address: { "@type": "PostalAddress", streetAddress: b.address, addressLocality: "Denver", addressRegion: "CO", addressCountry: "US" },
        telephone: b.phone || undefined,
        url: b.website || `${SITE}/directory/${b.slug}`,
        ...(b.lat && b.lng ? { geo: { "@type": "GeoCoordinates", latitude: b.lat, longitude: b.lng } } : {}),
        ...(b.rating && b.review_count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: b.rating, reviewCount: b.review_count } } : {}),
        areaServed: "River North Art District, Denver",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Explore RiNo", item: `${SITE}/directory` },
          { "@type": "ListItem", position: 3, name: b.category, item: `${SITE}/directory?category=${encodeURIComponent(b.category)}` },
          { "@type": "ListItem", position: 4, name: b.name, item: `${SITE}/directory/${b.slug}` },
        ],
      },
    ],
  };

  return (
    <div>
      <Seo title={b.name} description={b.tagline || b.description?.slice(0, 155)} image={absImg} type="article" jsonLd={jsonLd} canonicalPath={`/directory/${b.slug}`} />

      <nav aria-label="Breadcrumb" data-testid="breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 text-xs text-neutral-500">
        <Link to="/" className="hover:text-white">Home</Link><span className="mx-1.5">/</span>
        <Link to="/directory" className="hover:text-white">Explore RiNo</Link><span className="mx-1.5">/</span>
        <Link to={`/directory?category=${encodeURIComponent(b.category)}`} className="hover:text-white">{b.category}</Link><span className="mx-1.5">/</span>
        <span className="text-neutral-300">{b.name}</span>
      </nav>

      <div className="relative h-[46vh] overflow-hidden grain">
        <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
            <Link to="/directory" className="inline-flex items-center gap-2 text-neutral-300 hover:text-white text-sm mb-4" data-testid="back-to-directory"><ArrowLeft size={16} /> Directory</Link>
            <div className="flex items-center gap-3">
              <span className="font-accent text-[#00E5FF] text-sm">{b.category}</span>
              {b.featured && <span className="bg-[#FF0055] text-white text-[10px] font-accent px-2 py-1 flex items-center gap-1"><Star size={10} /> Featured</span>}
              {geoVerified && <span className="bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40 text-[10px] font-accent px-2 py-1 flex items-center gap-1" data-testid="geo-verified-badge"><MapPin size={10} /> Verified on location</span>}
            </div>
            <h1 className="font-display font-black text-4xl sm:text-6xl text-white mt-2">{b.name}</h1>
            <p className="text-neutral-300 text-lg mt-2">{b.tagline}</p>
            <div className="flex items-center gap-3 mt-5">
              <FollowButton kind="venue" subjectKey={b.slug} label={b.name} className="px-5 py-2.5" />
              <button onClick={toggleFav} data-testid="favorite-btn"
                className={`inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 border transition-colors ${faved ? "border-[#FF0055] text-[#FF0055]" : "border-white/20 text-white hover:border-[#FF0055]"}`}>
                <Heart size={16} fill={faved ? "#FF0055" : "none"} /> {faved ? "Saved" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display font-bold text-2xl text-white mb-4">About</h2>
          <p className="text-neutral-300 leading-relaxed text-lg">{b.description}</p>

          {b.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {b.tags.map((t) => <span key={t} className="border border-white/15 text-neutral-300 text-xs px-3 py-1.5">#{t}</span>)}
            </div>
          )}

          {gallery.length > 1 && (
            <div className="mt-10">
              <h2 className="font-display font-bold text-2xl text-white mb-4">Gallery</h2>
              <div className="grid grid-cols-2 gap-3">
                {gallery.map((g, i) => <img key={i} src={g} alt={`${b.name} ${i + 1}`} className="w-full h-56 object-cover border border-white/10" />)}
              </div>
            </div>
          )}

          {videos.length > 0 && (
            <div className="mt-10" data-testid="business-videos">
              <h2 className="font-display font-bold text-2xl text-white mb-4">Video</h2>
              <div className="grid grid-cols-1 gap-4">
                {videos.map((v) => (
                  <video key={v.id} src={v.url} controls poster={b.image} className="w-full border border-white/10 bg-black" />
                ))}
              </div>
            </div>
          )}

          {events.length > 0 && (
            <div className="mt-10" data-testid="business-events">
              <h2 className="font-display font-bold text-2xl text-white mb-4">Upcoming Events at {b.name}</h2>
              <div className="grid grid-cols-1 gap-5">
                {events.map((ev, i) => <EventCard key={ev.id} e={ev} index={i} />)}
              </div>
            </div>
          )}

          {guides.length > 0 && (
            <div className="mt-10" data-testid="business-guides">
              <h2 className="font-display font-bold text-2xl text-white mb-4">Featured in RiNo Guides</h2>
              <div className="space-y-2">
                {guides.map((g) => (
                  <Link key={g.id} to={`/guides/${g.slug}`} data-testid={`biz-guide-${g.slug}`}
                    className="flex items-center justify-between border border-white/10 bg-[#141414] p-4 hover:border-[#FF0055] transition-colors group">
                    <div>
                      <span className="font-accent text-[10px] text-[#00E5FF]">{g.category}</span>
                      <div className="text-white text-sm font-medium group-hover:text-[#FF0055]">{g.title}</div>
                    </div>
                    <ArrowUpRight size={16} className="text-neutral-500 group-hover:text-[#FF0055]" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          <Reviews slug={slug} />
        </div>

        <aside className="lg:col-span-1">
          <div className="border border-white/10 bg-[#141414] p-6 space-y-4 lg:sticky lg:top-24">
            <h3 className="font-accent text-[#00E5FF] text-sm">Visit</h3>
            {b.address && <div className="flex items-start gap-3 text-neutral-300 text-sm"><MapPin size={16} className="text-[#FF0055] mt-0.5" /> {b.address}</div>}
            {b.hours && <div className="flex items-start gap-3 text-neutral-300 text-sm"><Clock size={16} className="text-[#FF0055] mt-0.5" /> {b.hours}</div>}
            {b.phone && <a href={`tel:${b.phone}`} className="flex items-center gap-3 text-neutral-300 hover:text-white text-sm"><Phone size={16} className="text-[#FF0055]" /> {b.phone}</a>}
            {b.website && <a href={b.website} target="_blank" rel="noopener noreferrer" onClick={() => track("business", b.id, "click")} data-testid="business-website" className="flex items-center gap-3 text-neutral-300 hover:text-white text-sm"><Globe size={16} className="text-[#FF0055]" /> Website</a>}
            {b.instagram && <a href={`https://instagram.com/${b.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-neutral-300 hover:text-white text-sm"><Instagram size={16} className="text-[#FF0055]" /> {b.instagram}</a>}
          </div>

          {(b.menu_url || b.events_url) && (
            <div className="border border-white/10 bg-[#141414] p-6 space-y-3 mt-4" data-testid="venue-links">
              <h3 className="font-accent text-[#00E5FF] text-sm">Plan Ahead</h3>
              {b.menu_url && (
                <a href={b.menu_url} target="_blank" rel="noopener noreferrer" onClick={() => track("business", b.id, "menu")} data-testid="venue-menu-link"
                   className="flex items-center justify-between text-neutral-200 hover:text-white text-sm group">
                  <span className="flex items-center gap-3"><Utensils size={16} className="text-[#FF0055]" /> View Menu{b.menu_type === "document" ? " (PDF)" : ""}</span>
                  <ArrowUpRight size={15} className="text-neutral-500 group-hover:text-[#FF0055]" />
                </a>
              )}
              {b.events_url && (
                <a href={b.events_url} target="_blank" rel="noopener noreferrer" onClick={() => track("business", b.id, "events")} data-testid="venue-events-link"
                   className="flex items-center justify-between text-neutral-200 hover:text-white text-sm group">
                  <span className="flex items-center gap-3"><CalendarDays size={16} className="text-[#FF0055]" /> Events &amp; Calendar</span>
                  <ArrowUpRight size={15} className="text-neutral-500 group-hover:text-[#FF0055]" />
                </a>
              )}
              <p className="text-neutral-600 text-[10px] pt-1">Pulled live from the venue by the RiNo Ranger.</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
